import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ListTodo, 
  Plus, 
  Check, 
  Clock, 
  Calendar,
  Edit,
  Trash2,
  ArrowLeft,
  CheckCircle,
  Circle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tasks = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filter, setFilter] = useState("all"); // all, pending, completed

  const [form, setForm] = useState({
    title: "",
    description: "",
    due_date: "",
    priority: "medium",
    status: "pending"
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    if (taskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setSelectedTask(task);
        setForm(task);
        setShowDialog(true);
      }
    }
  }, [taskId, tasks]);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API}/tasks`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTasks(data?.tasks ?? data ?? []);
      }
    } catch {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = selectedTask ? `${API}/tasks/${selectedTask.id}` : `${API}/tasks`;
      const method = selectedTask ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(selectedTask ? "Task updated" : "Task created");
        fetchTasks();
        closeDialog();
      } else {
        toast.error("Failed to save task");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleComplete = async (task) => {
    try {
      const response = await fetch(`${API}/tasks/${task.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...task, status: task.status === "completed" ? "pending" : "completed" })
      });

      if (response.ok) {
        toast.success(task.status === "completed" ? "Task reopened" : "Task completed!");
        fetchTasks();
      }
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDelete = async (taskId) => {
    if (!confirm("Delete this task?")) return;
    
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Task deleted");
        fetchTasks();
      }
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const openDialog = (task = null) => {
    setSelectedTask(task);
    setForm(task || {
      title: "",
      description: "",
      due_date: "",
      priority: "medium",
      status: "pending"
    });
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setSelectedTask(null);
    setForm({
      title: "",
      description: "",
      due_date: "",
      priority: "medium",
      status: "pending"
    });
    if (taskId) {
      navigate("/tasks");
    }
  };

  const filteredTasks = (tasks || []).filter(t => {
    if (filter === "all") return true;
    return t.status === filter;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "text-[#EF4444]";
      case "medium": return "text-[#F59E0B]";
      case "low": return "text-[#22C55E]";
      default: return "text-[#71717A]";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-[#1A1A1D] rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-[#A1A1AA]" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#FAFAFA]">Tasks</h1>
            <p className="text-sm text-[#A1A1AA]">Manage your to-do list</p>
          </div>
        </div>
        <Button onClick={() => openDialog()} className="btn-gold" data-testid="add-task-btn">
          <Plus size={20} className="mr-2" />
          Add Task
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {["all", "pending", "completed"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? "btn-gold" : "border-[#27272A] text-[#A1A1AA]"}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {filteredTasks.map((task) => (
          <Card 
            key={task.id}
            className={`bg-[#121214] border-[#27272A] cursor-pointer hover:border-[#D4AF37]/50 transition-all ${
              task.status === "completed" ? "opacity-60" : ""
            }`}
            data-testid={`task-card-${task.id}`}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleComplete(task)}
                  className="flex-shrink-0"
                  data-testid={`complete-${task.id}`}
                >
                  {task.status === "completed" ? (
                    <CheckCircle size={24} className="text-[#22C55E]" />
                  ) : (
                    <Circle size={24} className="text-[#71717A] hover:text-[#D4AF37]" />
                  )}
                </button>
                
                <div 
                  className="flex-1 cursor-pointer"
                  onClick={() => openDialog(task)}
                >
                  <h3 className={`font-medium ${task.status === "completed" ? "line-through text-[#71717A]" : "text-[#FAFAFA]"}`}>
                    {task.title}
                  </h3>
                  {task.description && (
                    <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-1">{task.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2">
                    {task.due_date && (
                      <span className="text-xs text-[#71717A] flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    )}
                    <span className={`text-xs ${getPriorityColor(task.priority)} capitalize`}>
                      {task.priority} priority
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openDialog(task)}
                    className="p-2 hover:bg-[#1A1A1D] rounded-lg text-[#A1A1AA] hover:text-[#D4AF37]"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(task.id)}
                    className="p-2 hover:bg-[#1A1A1D] rounded-lg text-[#A1A1AA] hover:text-[#EF4444]"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <ListTodo size={48} className="text-[#71717A] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No tasks found</p>
            <Button onClick={() => openDialog()} className="mt-4 btn-gold">
              Create your first task
            </Button>
          </div>
        )}
      </div>

      {/* Task Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#121214] border-[#27272A] text-[#FAFAFA]">
          <DialogHeader>
            <DialogTitle>{selectedTask ? "Edit Task" : "New Task"}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Title *</Label>
              <Input
                name="title"
                value={form.title || ""}
                onChange={handleChange}
                required
                className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                placeholder="Task title"
              />
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Description</Label>
              <Textarea
                name="description"
                value={form.description || ""}
                onChange={handleChange}
                className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                placeholder="Task details..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Due Date</Label>
                <Input
                  name="due_date"
                  type="date"
                  value={form.due_date || ""}
                  onChange={handleChange}
                  className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Priority</Label>
                <select
                  name="priority"
                  value={form.priority || "medium"}
                  onChange={handleChange}
                  className="mt-1 w-full px-3 py-2 bg-[#1A1A1D] border border-[#27272A] rounded-md text-[#FAFAFA]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={closeDialog} className="flex-1 border-[#27272A]">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-gold">
                {selectedTask ? "Save Changes" : "Create Task"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tasks;
